import { type z } from 'zod'

import {
  accept,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type TurnState,
} from '../engine.js'
import { createRng, type Rng } from '../rng.js'
import {
  DEFAULT_ROUND_LENGTH,
  mathsAction,
  mathsEngine,
  toMathsView,
  type MathsAction,
  type MathsState,
} from './maths.js'
import { SKILLS, type SkillLevels } from './maths-skills.js'

/**
 * Monkey's Maths, played by everybody at once.
 *
 * Not a turn-based game with the turns sped up. Turn-taking is right for
 * Hangman, where one shared word is being worked out together, but applied to
 * arithmetic it would leave three children watching a fourth do a sum. So each
 * player gets their own board and they all run at the same time.
 *
 * Everybody at once, but not against each other. What the room gets is added
 * up between them, and nobody's total is set beside anybody else's — the
 * television used to withhold standings mid-round precisely because a child who
 * can see she is losing from question two stops trying, and the honest version
 * of that thought is not to keep score at all.
 *
 * Every board is dealt from one seed and one set of levels, so everyone is
 * asked the same questions in the same order. Boards diverge only where a
 * player gets something wrong and it comes back for them — which is the point
 * of the game, and is theirs alone rather than something the room waits for.
 */

/** How hard the questions are, chosen by whoever set the race up. */
export const RACE_LEVELS = ['warm-up', 'just-right', 'really-tricky'] as const

export type RaceLevel = (typeof RACE_LEVELS)[number]

/**
 * Where each setting sits on the skill scale.
 *
 * A race uses one level for everybody rather than each player's own, because
 * questions are picked by level: personal levels would mean personal questions,
 * and racing somebody over different sums is not a race.
 */
const LEVEL_FOR: Readonly<Record<RaceLevel, number>> = {
  'warm-up': 1,
  'just-right': 4,
  'really-tricky': 7,
}

/** Puts every skill at one level, so the whole roster is picked from evenly. */
export function levelsForRace(level: RaceLevel): SkillLevels {
  return Object.fromEntries(SKILLS.map((skill) => [skill.key, LEVEL_FOR[level]]))
}

export interface MathsRaceConfig {
  readonly level: RaceLevel
  readonly roundLength?: number
  /** When the race was dealt. Fluency is judged against it, as in solo play. */
  readonly startedAt: number
}

export interface MathsRaceState {
  readonly status: 'playing' | 'finished'
  /**
   * The roster, fixed when the race starts.
   *
   * `currentPlayerId` is always null: nobody is waiting for a turn. It is kept
   * so the roster is in the same place every other game keeps it, which is what
   * lets the room record results without knowing which game it ran.
   */
  readonly turn: TurnState
  readonly startedAt: number
  readonly roundLength: number
  readonly level: RaceLevel
  /** One board per player, keyed by player id. */
  readonly boards: Readonly<Record<string, MathsState>>
}

/** What one player's race looks like to everybody. */
export interface MathsRacePlayerView {
  readonly playerId: string
  readonly displayName: string
  /** Their current question. Not a secret: everyone is being asked the same ones. */
  readonly prompt: string | null
  /**
   * How many questions they have worked through.
   *
   * Deliberately attempts rather than answers-got-right. How far along somebody
   * is belongs on the television; who is winning does not, because a child who
   * can see she is losing from question two stops trying. Attempts rise faster
   * when a player is struggling, so this cannot be read as a running score.
   *
   * There is deliberately no "finished" flag beside it either. Attempts are a
   * noisy signal and that is what makes them safe to show; finishing is not
   * noisy at all, and a tick against one child's name while another is still
   * going is the plainest way there is of saying who is winning. Whether she
   * has finished is in her own payload, where it is nobody else's business.
   */
  readonly asked: number
}

export interface MathsRaceView {
  readonly status: MathsRaceState['status']
  readonly roundLength: number
  readonly level: RaceLevel
  readonly players: readonly MathsRacePlayerView[]
  /**
   * The recipient's own board, if they are playing.
   *
   * Filled in per client rather than broadcast: how she is getting on is hers,
   * and putting it in the shared payload would put it on the television beside
   * everybody else's.
   */
  readonly you: MathsRaceYou | null
  /**
   * What the room got between them, once it is over.
   *
   * One number for everybody, not one each. They were answering the same
   * questions at the same time, and adding it up is the version of that where
   * nobody comes last.
   */
  readonly together: RaceTotal | null
}

export interface RaceTotal {
  readonly correct: number
  /** Everyone's rounds added together, so the total has something to be out of. */
  readonly outOf: number
}

export interface MathsRaceYou {
  readonly playerId: string
  readonly prompt: string | null
  readonly correct: number
  readonly finished: boolean
  readonly lastAnswer: ReturnType<typeof toMathsView>['lastAnswer']
}

export type MathsRaceAction = MathsAction

export const mathsRaceAction: z.ZodType<MathsRaceAction> = mathsAction

export const mathsRaceEngine: GameEngine<MathsRaceConfig, MathsRaceState, MathsRaceAction> = {
  key: 'maths',
  actionSchema: mathsRaceAction,

  createInitialState(config, players, rng: Rng): MathsRaceState {
    const roundLength = config.roundLength ?? DEFAULT_ROUND_LENGTH
    const levels = levelsForRace(config.level)

    // One seed drawn from the room's randomness and then used for every board,
    // so the questions match. Drawing a seed per player would deal everybody a
    // different race.
    const seed = String(rng.int(2 ** 31))

    const boards = Object.fromEntries(
      players.map((player) => [
        player.id,
        mathsEngine.createInitialState(
          { levels, roundLength, startedAt: config.startedAt },
          [player],
          createRng(seed),
        ),
      ]),
    )

    return {
      status: 'playing',
      turn: { players, currentPlayerId: null },
      startedAt: config.startedAt,
      roundLength,
      level: config.level,
      boards,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')

    const board = state.boards[context.playerId]
    // Somebody who arrived after the race started. They have no board, and
    // dealing them one now would put them on a different question from
    // everybody else. They wait for the next race.
    if (!board) return reject(state, 'unknown-player')

    // Finished her round and is watching the others. Not an error, and not
    // game-over either — the race is still running.
    if (mathsEngine.isTerminal(board)) return reject(state, 'already-played')

    const outcome = mathsEngine.reduce(board, action, context)
    if (!outcome.ok) return reject(state, outcome.reason)

    const boards = { ...state.boards, [context.playerId]: outcome.state }
    const everyoneDone = Object.values(boards).every((each) => mathsEngine.isTerminal(each))

    return accept({ ...state, boards, status: everyoneDone ? 'finished' : 'playing' })
  },

  isTerminal: (state) => state.status !== 'playing',

  summarise(state, endedAt): PlaySummary {
    // Won if everybody finished the round they were dealt. A race that ran out
    // of time with people still working is not a loss — nobody failed at
    // anything — so it is recorded as abandoned.
    const everyoneFinished = Object.values(state.boards).every(
      (board) => mathsEngine.summarise(board, endedAt).result === 'won',
    )

    return {
      gameKey: 'maths',
      result: everyoneFinished ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: state.level,
    }
  },
}

/**
 * Ends a race that is still running.
 *
 * Used when the round's time is up, or when everybody still connected has
 * finished and the only boards left belong to phones in pockets. Without it a
 * race waits forever for somebody who has gone, and the room is stuck on a
 * screen nobody can move past.
 *
 * Whatever each player had done stands. Being interrupted is not being wrong.
 */
export function endRace(state: MathsRaceState): MathsRaceState {
  if (state.status !== 'playing') return state
  return { ...state, status: 'finished' }
}

/**
 * What a client is allowed to see.
 *
 * `forPlayerId` is the recipient, so her own board comes back with the detail
 * she needs to play and nobody else's does. The room sends this per client
 * rather than broadcasting one payload, which is the only way progress can be
 * public while standings stay private.
 */
export function toMathsRaceView(state: MathsRaceState, forPlayerId: string | null): MathsRaceView {
  const players = state.turn.players.map((player): MathsRacePlayerView => {
    const board = state.boards[player.id]
    const view = board ? toMathsView(board) : null

    return {
      playerId: player.id,
      displayName: player.displayName,
      prompt: view?.prompt ?? null,
      asked: board?.attempts.length ?? 0,
    }
  })

  const own = forPlayerId === null ? undefined : state.boards[forPlayerId]
  const ownView = own ? toMathsView(own) : null

  return {
    status: state.status,
    roundLength: state.roundLength,
    level: state.level,
    players,
    you:
      own && ownView && forPlayerId
        ? {
            playerId: forPlayerId,
            prompt: ownView.prompt,
            correct: ownView.correct,
            finished: mathsEngine.isTerminal(own),
            lastAnswer: ownView.lastAnswer,
          }
        : null,
    // Held back until it is over, and then it is one number for the room.
    together: state.status === 'finished' ? roomTotal(state) : null,
  }
}

/** What everybody got between them. */
function roomTotal(state: MathsRaceState): RaceTotal {
  const boards = Object.values(state.boards)

  return {
    correct: boards.reduce((total, board) => total + board.correctCount, 0),
    outOf: boards.length * state.roundLength,
  }
}
